import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

export type Location = Tables<'locations'>;
export type LocationInsert = TablesInsert<'locations'>;
export type LocationUpdate = TablesUpdate<'locations'>;

export interface LocationWithStats extends Location {
  customer_count?: number;
  product_count?: number;
  total_sales?: number;
  total_revenue?: number;
}

export interface LocationFilters {
  companyId: string;
  isActive?: boolean;
  searchTerm?: string;
}

export const useLocations = () => {
  const { companyId } = useAuth();
  const { toast } = useToast();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debug logging removed to prevent performance issues

  const fetchLocations = async (filters?: LocationFilters) => {
    const targetCompanyId = filters?.companyId || companyId;
    
    if (!targetCompanyId) {
      setLoading(false);
      setError('Şirket ID bulunamadı. Lütfen yöneticinizle iletişime geçin.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('locations')
        .select(`
          *,
          manager:user_profiles!locations_manager_user_id_fkey(id, full_name, email)
        `)
        .eq('company_id', targetCompanyId)
        .order('is_default', { ascending: false })
        .order('name');

      // Apply active filter
      if (filters?.isActive !== undefined) {
        query = query.eq('is_active', filters.isActive);
      }

      // Apply search filter
      if (filters?.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        query = query.ilike('name', `%${searchLower}%`);
      }

      const { data: locationsData, error: locationsError } = await query;

      if (!locationsError) {
        setLocations(locationsData || []);
        return;
      }

      // Fallback: Some environments can still fail relation embedding
      // (e.g. stale PostgREST schema cache). Keep locations usable without manager details.
      let fallbackQuery = supabase
        .from('locations')
        .select('*')
        .eq('company_id', targetCompanyId)
        .order('is_default', { ascending: false })
        .order('name');

      if (filters?.isActive !== undefined) {
        fallbackQuery = fallbackQuery.eq('is_active', filters.isActive);
      }

      if (filters?.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        fallbackQuery = fallbackQuery.ilike('name', `%${searchLower}%`);
      }

      const { data: fallbackData, error: fallbackError } = await fallbackQuery;
      if (fallbackError) {
        throw locationsError;
      }

      setLocations(fallbackData || []);

    } catch (err: any) {
      const errorMessage = err.message || 'Lokasyonlar yüklenirken hata oluştu';
      setError(errorMessage);
      console.error('Error fetching locations:', err);
      toast({
        title: "Hata",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getLocationById = async (locationId: string): Promise<Location | null> => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select(`
          *,
          manager:user_profiles!locations_manager_user_id_fkey(id, full_name, email)
        `)
        .eq('id', locationId)
        .single();

      if (!error) {
        return data;
      }

      const { data: fallbackData, error: fallbackError } = await supabase
        .from('locations')
        .select('*')
        .eq('id', locationId)
        .single();

      if (fallbackError) {
        console.error('Error fetching location:', error);
        return null;
      }

      return fallbackData;
    } catch (error) {
      console.error('Error in getLocationById:', error);
      return null;
    }
  };

  const getLocationWithStats = async (locationId: string): Promise<LocationWithStats | null> => {
    try {
      // Fetch location details
      const location = await getLocationById(locationId);
      if (!location) return null;

      // Fetch statistics in parallel
      // Note: customers are now universal across company, so we get all customers for company
      const [
        { count: customerCount },
        { count: productCount },
        { count: totalSales, data: salesData }
      ] = await Promise.all([
        supabase
          .from('customers')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', location.company_id),
        supabase
          .from('products')
          .select('*', { count: 'exact', head: true })
          .eq('location_id', locationId)
          .is('exit_date', null),
        supabase
          .from('sales')
          .select('total_net')
          .eq('location_id', locationId)
          .eq('status', 'completed')
      ]);

      const totalRevenue = salesData?.reduce((sum, sale) => sum + (sale.total_net || 0), 0) || 0;

      return {
        ...location,
        customer_count: customerCount || 0,
        product_count: productCount || 0,
        total_sales: totalSales || 0,
        total_revenue: totalRevenue
      };
    } catch (error) {
      console.error('Error fetching location with stats:', error);
      return null;
    }
  };

  const createLocation = async (locationData: LocationInsert): Promise<Location | null> => {
    if (!companyId) {
      toast({
        title: "Hata",
        description: "Şirket ID bulunamadı",
        variant: "destructive",
      });
      return null;
    }

    try {
      // Ensure company_id is set
      const newLocationData = {
        ...locationData,
        company_id: companyId
      };

      const { data, error } = await supabase
        .from('locations')
        .insert([newLocationData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      toast({
        title: "Başarılı",
        description: "Lokasyon başarıyla oluşturuldu",
      });

      // Refresh locations list
      fetchLocations({ companyId });

      return data;
    } catch (error: any) {
      console.error('Error creating location:', error);
      toast({
        title: "Hata",
        description: error.message || "Lokasyon oluşturulurken hata oluştu",
        variant: "destructive",
      });
      return null;
    }
  };

  const updateLocation = async (locationId: string, updates: LocationUpdate): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('locations')
        .update(updates)
        .eq('id', locationId);

      if (error) {
        throw error;
      }

      toast({
        title: "Başarılı",
        description: "Lokasyon başarıyla güncellendi",
      });

      // Refresh locations list
      if (companyId) {
        fetchLocations({ companyId });
      }

      return true;
    } catch (error: any) {
      console.error('Error updating location:', error);
      toast({
        title: "Hata",
        description: error.message || "Lokasyon güncellenirken hata oluştu",
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteLocation = async (locationId: string): Promise<boolean> => {
    try {
      // Check if this is the default location
      const location = await getLocationById(locationId);
      if (location?.is_default) {
        toast({
          title: "Hata",
          description: "Varsayılan lokasyon silinemez",
          variant: "destructive",
        });
        return false;
      }

      // Check if location has data (products, sales) - customers are universal now
      const [
        { count: productCount },
        { count: salesCount }
      ] = await Promise.all([
        supabase
          .from('products')
          .select('*', { count: 'exact', head: true })
          .eq('location_id', locationId),
        supabase
          .from('sales')
          .select('*', { count: 'exact', head: true })
          .eq('location_id', locationId)
      ]);

      if ((productCount || 0) > 0 || (salesCount || 0) > 0) {
        toast({
          title: "Hata",
          description: "Bu lokasyonda ürün veya satış bulunduğu için silinemez. Önce lokasyonu pasif hale getirin.",
          variant: "destructive",
        });
        return false;
      }

      const { error } = await supabase
        .from('locations')
        .delete()
        .eq('id', locationId);

      if (error) {
        throw error;
      }

      toast({
        title: "Başarılı",
        description: "Lokasyon başarıyla silindi",
      });

      // Refresh locations list
      if (companyId) {
        fetchLocations({ companyId });
      }

      return true;
    } catch (error: any) {
      console.error('Error deleting location:', error);
      toast({
        title: "Hata",
        description: error.message || "Lokasyon silinirken hata oluştu",
        variant: "destructive",
      });
      return false;
    }
  };

  const setDefaultLocation = async (locationId: string): Promise<boolean> => {
    if (!companyId) return false;

    try {
      // First, remove default flag from all locations of this company
      await supabase
        .from('locations')
        .update({ is_default: false })
        .eq('company_id', companyId);

      // Then set the selected location as default
      const { error } = await supabase
        .from('locations')
        .update({ is_default: true })
        .eq('id', locationId);

      if (error) {
        throw error;
      }

      toast({
        title: "Başarılı",
        description: "Varsayılan lokasyon güncellendi",
      });

      // Refresh locations list
      fetchLocations({ companyId });

      return true;
    } catch (error: any) {
      console.error('Error setting default location:', error);
      toast({
        title: "Hata",
        description: error.message || "Varsayılan lokasyon ayarlanırken hata oluştu",
        variant: "destructive",
      });
      return false;
    }
  };

  const getDefaultLocation = (): Location | null => {
    return locations.find(location => location.is_default && location.is_active) || null;
  };

  const getActiveLocations = (): Location[] => {
    return locations.filter(location => location.is_active);
  };

  // Fetch locations on mount and when companyId changes
  useEffect(() => {
    if (companyId) {
      fetchLocations({ companyId, isActive: true });
    }
  }, [companyId]);

  return {
    locations,
    loading,
    error,
    fetchLocations,
    getLocationById,
    getLocationWithStats,
    createLocation,
    updateLocation,
    deleteLocation,
    setDefaultLocation,
    getDefaultLocation,
    getActiveLocations,
    refetch: () => companyId ? fetchLocations({ companyId }) : null
  };
};
